<template>
    <div class="container">
        <v-title :lineage="[{'Products':'products'}]">
            {{isFormUpdating ? 'Update' : 'Add'}} Product
            <div slot="actions">
                <v-cancel :defaultRoute="{name: 'products'}"></v-cancel>
                <r-button
                    v-if="isFormUpdating"
                    type="primary"
                    @click="save"
                    :disabled="isLoading">Save Product</r-button>
                <r-button
                    v-else
                    type="primary"
                    @click="save"
                    :loading="isLoading">Create Pricing Plan</r-button>
            </div>
        </v-title>
        <v-error-messages
            :has-frontend-errors="$v.product.$error"
            :server-error="serverError"
            class="stack-l">
            <li class="list-item" v-if="!$v.product.name.required">Product name is required</li>
            <li class="list-item" v-if="!$v.product.id.resourceId"><span>The product ID cannot contain spaces or special characters other than <strong>@ ~ _ -</strong> and should be less than <strong>50</strong> characters long.</span></li>
            <li class="list-item" v-if="!$v.product.description.maxLength">The product must be less than {{maxDescriptionLength}} characters long.</li>
        </v-error-messages>
        <v-form-tile>
            <h2 slot="title">Product Information</h2>
            <p slot="description" class="text-muted">A product is something that your customers buy from you. You'll add plans next to determine your product's pricing, which can either be on a subscription or one-time basis. Learn more about how to set up products and pricing plans <a target="_blank" tabindex="-1" href="https://help.rebilly.com/rebilly-basics/create-a-product-and-a-pricing-plan">here</a>.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                    v-model="product.name"
                                    label="Product Name"
                                    auto-focus
                                    help-text="As displayed to your customers on invoices and receipts."
                                    :validate="$v.product.name"
                                    class="slug-source"
                            />
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                v-model="product.id"
                                label="Product ID"
                                help-text="A unique identifier for your product, used to reference it (e.g. gold_001)"
                                :validate="$v.product.id"
                                :disabled="isFormUpdating"
                                class="slug-target"
                            />
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                multiline
                                rows="3"
                                v-model="product.description"
                                label="Product Description"
                                help-text="As displayed to your customers on invoices and receipts."
                                :max-length="maxDescriptionLength"
                                :validate="$v.product.description"
                            />
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                v-model="product.unitLabel"
                                label="Unit Label"
                                help-text="The unit related to the item quantity that will appear on invoices and receipts (such as a seat, API call, or a widget)"
                            />
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                v-model="product.accountingCode"
                                label="Accounting Code"
                                help-text="Optional: The accounting code may help map data import into your accounting software."
                            />
                        </div>
                        <v-custom-fields-manager
                            resource-type="products"
                            v-model="product.customFields"/>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Taxes</h2>
            <p slot="description" class="text-muted">If your product is taxable, select the appropriated tax category, and taxes will be calculated using the TaxJar tax calculator.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-checkbox
                                label="Is Taxable"
                                :value="true"
                                v-model="isTaxable"
                            />
                        </div>
                        <div v-if="isTaxable" class="grid-item grid-item-half grid-item-break-half">
                            <v-select
                                label="Tax Category ID"
                                :options="taxCategoriesOptions"
                                :custom-label="nameWithTaxCode"
                                option-label="name"
                                option-key="taxCode"
                                placeholder="Select Tax Category ID"
                                v-model="product.taxCategoryId"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Fulfillment</h2>
            <p slot="description" class="text-muted">If your product ships to customers, select the appropriate options here. Shipping rates are calculated through Shipping Zones.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-full">
                            <r-checkbox
                                label="Is Shippable"
                                :caption="shippingZoneRequired"
                                :value="true"
                                v-model="product.requiresShipping"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <div class="grid-fitted stack-l">
            <div class="grid-item text-right equal-widths-elements-mobile">
                <v-cancel :defaultRoute="{name: 'products'}"></v-cancel>
                <r-button
                    v-if="isFormUpdating"
                    type="primary"
                    @click="save"
                    :disabled="isLoading">Save Product</r-button>
                <r-button
                    v-else
                    type="primary"
                    @click="save"
                    :loading="isLoading">Create Pricing Plan</r-button>
            </div>
        </div>
    </div>
</template>

<script>
    import vTitle from '@/components/v-title';
    import {
        vCancel, vIconSprites, vFormTile, vSelect, vIcon, vErrorMessages,
    } from '@/components/form';
    import {required, maxLength} from 'vuelidate/lib/validators';
    import resourceId from '@/components/vuelidate/validators/resource-id';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';
    import sluggifyMixin from '@/mixins/sluggify-mixin';
    import ProductModel from '@/common/models/product-model';
    import taxCategories from '@/data/tax-categories';
    import vCustomFieldsManager from '@/components/custom-fields/v-custom-fields-manager';

    export default {
        name: 'product-form',
        mixins: [
            FormLoaderMixin,
            sluggifyMixin({
                target: 'product.id',
                source: 'product.name',
                entityCallback(id) {
                    return this.$api(({products}) => products.get({id}));
                },
            }),
        ],
        components: {
            vTitle,
            vIconSprites,
            vFormTile,
            vCancel,
            vSelect,
            vIcon,
            vErrorMessages,
            vCustomFieldsManager,
        },
        props: {
            id: {
                type: [String, Number],
                default: null,
            },
        },
        data() {
            return {
                product: new ProductModel(),
                taxCategoriesOptions: taxCategories,
                shippingZone: [],
                maxDescriptionLength: 512,
            };
        },
        validations() {
            return {
                product: {
                    name: {required},
                    id: {resourceId},
                    description: {
                        maxLength: maxLength(this.maxDescriptionLength),
                    },
                },
            };
        },
        computed: {
            isTaxable: {
                get() {
                    return this.product.taxCategoryId !== null;
                },
                set(value) {
                    this.product.taxCategoryId = !value ? null : '00000';
                },
            },
            shippingZoneRequired() {
                return !this.shippingZone.length
                    ? 'If your product is shippable you have to define your shipping zones (see settings \u2192 shipping zones)'
                    : '';
            },
        },
        methods: {
            nameWithTaxCode({name, taxCode}) {
                return `${name} (${taxCode})`;
            },
            async getShippingZone() {
                try {
                    const {items} = await this.$api(({shippingZones}) => shippingZones.getAll({limit: 1}));
                    this.shippingZone = items;
                } catch (error) {
                    throw error;
                }
            },
            async save() {
                this.$v.$touch();
                if (!this.$v.product.$error) {
                    this.isLoading = true;
                    const params = {
                        data: this.product.getPayload(),
                    };
                    if (this.product.id) {
                        params.id = this.product.id;
                    }
                    const method = this.isFormUpdating ? 'update' : 'create';
                    try {
                        const product = await this.$api(({products}) => products[method](params));
                        this.$store.dispatch('rulesEngine/invalidateProducts');
                        if (!this.isFormUpdating) {
                            window.scrollTo(0, 0);
                            this.$router.push({name: 'add-plan', params: {productId: product.fields.id, productUnit: product.fields.unitLabel}});
                            this.$toast.positive(`Product successfully created`);
                            this.triggerIntercomEvent({
                                name: 'Created a product',
                                meta: {
                                    product: product.fields.name,
                                },
                            });
                        } else {
                            this.$router.push({name: 'products'});
                            this.$toast.positive(`Product successfully updated`);
                            this.triggerIntercomEvent({
                                name: 'Updated a product',
                                meta: {
                                    name: product.fields.name,
                                },
                            });
                        }
                    } catch (error) {
                        this.serverError = error;
                    } finally {
                        this.isLoading = false;
                    }
                }
            },
            async fetchData() {
                this.getShippingZone();
                if (!this.isFormUpdating) {
                    return;
                }
                try {
                    const product = await this.$api(({products}) => products.get({id: this.id}));
                    this.product = new ProductModel(product.fields);
                } catch (error) {
                    this.$toast.fromError(error, `Product could not be created`);
                    this.$redirectOnError(error, {name: 'products'});
                }
            },
        },
    };
</script>
